import { useMemo } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { AddTile } from "./AddTile";
import { FileTile, categorize } from "./FileTile";
import { SkeletonTile } from "./SkeletonTile";
import { useTetrisMasonry } from "../../hooks/useTetrisMasonry";

const ADD_TILE_ID = "__add_tile__";

export function Masonry({
  files,
  uploads = [],
  mediaUrls,
  canDelete,
  searchQuery = "",
  searchFlashTick = 0,
  onPick,
  onOpen,
  onDownload,
  onDelete,
  onReorder,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } })
  );

  const itemIds = useMemo(
    () => [ADD_TILE_ID, ...files.map((f) => f.id), ...uploads.map((u) => u.id)],
    [files, uploads]
  );

  // Only shrink-to-fit tiles (notes, code, archive, binary, pdf, audio without
  // an artwork thumb) are eligible to grow into adjacent empty space. Media
  // tiles have an intrinsic width and would distort if stretched.
  const expandableIds = useMemo(() => {
    const ids = new Set();
    for (const f of files) {
      const kind = categorize(f);
      if (kind !== "image" && kind !== "video") ids.add(f.id);
    }
    return ids;
  }, [files]);

  const { containerRef, registerTile, positions, widths, height, ready } =
    useTetrisMasonry(itemIds, expandableIds);

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (active.id === ADD_TILE_ID || over.id === ADD_TILE_ID) return;
    const oldIndex = files.findIndex((f) => f.id === active.id);
    const newIndex = files.findIndex((f) => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(files, oldIndex, newIndex);
    onReorder(next);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={files.map((f) => f.id)} strategy={rectSortingStrategy}>
        <div
          ref={containerRef}
          className="relative w-full"
          style={{ height: ready ? height : undefined, minHeight: 200 }}
        >
          <AddTile
            onPick={onPick}
            tileRef={(el) => registerTile(ADD_TILE_ID, el)}
            position={positions[ADD_TILE_ID]}
            ready={ready}
          />
          {files.map((file) => (
            <FileTile
              key={file.id}
              file={file}
              mediaUrl={mediaUrls[file.id]}
              canDelete={canDelete}
              onOpen={onOpen}
              onDownload={onDownload}
              onDelete={onDelete}
              tileRef={(el) => registerTile(file.id, el)}
              position={positions[file.id]}
              expandedWidth={widths[file.id]}
              ready={ready}
              searchQuery={searchQuery}
              searchFlashTick={searchFlashTick}
            />
          ))}
          {uploads.map((upload) => (
            <SkeletonTile
              key={upload.id}
              upload={upload}
              tileRef={(el) => registerTile(upload.id, el)}
              position={positions[upload.id]}
              ready={ready}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
